// Agent configurations and the slots they run in on a project.
// Mirrors the /agent-configs and /projects/agent-configs routes; see
// docs/Y2026-Q4/design-agent-configs.md sections 3, 5 and 11.
import { useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { AgentSlotContext, DEFAULT_SLOT } from "../agentSlot";
import type { PostJsonError } from "../http/postJson";
import { useAgentsHttp } from "../useAgentsHttp";

export type SlotStart = "branch" | "cold";
export type SlotStatus = "active" | "paused" | "unavailable";

/** A project a config is switched on for, as /agent-configs/list gives it. */
export interface ConfigProject {
  project_id: string;
  project_name: string;
  slot: string;
  status: SlotStatus;
  paused_reason: string | null;
}

/** One platform of a config's plan, with its spend toward any cap. */
export interface PlatformLegStatus {
  platform: string;
  label: string;
  cap_usd: number | null;
  since: string | null;
  spent_usd: number | null;
  state: "in_use" | "cap_reached" | "not_configured" | "not_in_this_env" | "next";
}

export interface AgentConfig {
  key: string;
  label: string;
  description: string;
  allowed_in_production: boolean;
  /** The platforms its calls run on, in order. */
  platform_plan?: PlatformLegStatus[];
  /** What it costs beside default, in a sentence. */
  cost_note?: string;
  /** Only on /agent-configs/list. */
  projects?: ConfigProject[];
  /** The start modes the config offers here, on /agent-configs/list and for an
   * AvailableConfig. */
  starts?: SlotStart[];
}

/** A config not yet on for the project, with the start modes still open to it. */
export interface AvailableConfig extends AgentConfig {
  starts: SlotStart[];
}

/** A config switched on for one project. `default` is always first. */
export interface AgentSlot {
  slot: string;
  config_key: string;
  label: string;
  start: SlotStart;
  status: SlotStatus;
  paused_reason: string | null;
  branched_at: string | null;
  created_by: string | null;
  created_at: string | null;
  /** False where this environment may not run the config: it can only be removed. */
  available?: boolean;
  unavailable_reason?: string | null;
}

/** One slot's outcome of "Run every config now". */
export interface RunAllResult {
  slot: string;
  status?: string;
  run_id?: string | null;
  request_ids?: string[];
  error?: string;
}

export interface ProjectAgentConfigs {
  slots: AgentSlot[];
  available: AvailableConfig[];
}

export interface EnableManyResult {
  project_id: string;
  slot?: AgentSlot;
  error?: string;
}

const QK_CONFIGS = "inrange-agents-agent-configs";
const QK_PROJECT_CONFIGS = "inrange-agents-project-agent-configs";

/** The server's own sentence for a refusal ("default is working the project"), not the
 * status line and JSON around it. */
const refusal = (err: unknown): Error => {
  const body = (err as PostJsonError).bodyText;
  if (body) {
    try {
      const parsed = JSON.parse(body) as { message?: unknown };
      if (typeof parsed.message === "string" && parsed.message) return new Error(parsed.message);
    } catch {
      // Not JSON: the error as it came is the best there is.
    }
  }
  return err instanceof Error ? err : new Error(String(err));
};

const orRefusal = <T>(call: Promise<T>): Promise<T> =>
  call.catch((err: unknown) => {
    throw refusal(err);
  });

export const useAgentConfigs = () => {
  const http = useAgentsHttp();
  return useQuery<{ configs: AgentConfig[] }>([QK_CONFIGS], () =>
    http<{ configs: AgentConfig[] }>("/agent-configs/list", {}),
  );
};

/** Whether any project runs a slot other than default, so a cross-project page, which
 * shows default only, needs to say so. */
export const useOtherSlotsInUse = (): boolean => {
  const configs = useAgentConfigs().data?.configs ?? [];
  return configs.some((c) => (c.projects ?? []).some((p) => p.slot !== DEFAULT_SLOT));
};

export const useProjectAgentConfigs = (projectId: string) => {
  const http = useAgentsHttp();
  return useQuery<ProjectAgentConfigs>(
    [QK_PROJECT_CONFIGS, projectId],
    () => http<ProjectAgentConfigs>("/projects/agent-configs", { project_id: projectId }),
    { enabled: Boolean(projectId) },
  );
};

/** When the slot in view branched from default; null for default or a cold start. A row
 * the branch copied keeps its time, so one from before this was written in default. */
export const useBranchedAt = (): string | null => {
  const scope = useContext(AgentSlotContext);
  const slots = useProjectAgentConfigs(scope?.projectId ?? "").data?.slots;
  if (!scope || scope.slot === DEFAULT_SLOT) return null;
  const slot = slots?.find((s) => s.slot === scope.slot);
  return slot?.start === "branch" ? slot.branched_at : null;
};

const useProjectConfigsMutation = <TVars, TResp>(
  projectId: string,
  path: string,
) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<TResp, Error, TVars>(
    (vars) => orRefusal(http<TResp>(path, { project_id: projectId, ...vars })),
    {
      // The form shows the refusal beside the control that caused it.
      meta: { silentError: true },
      // The team page lists every project's slots, so it refreshes too.
      onSettled: () =>
        Promise.all([
          qc.invalidateQueries([QK_CONFIGS]),
          qc.invalidateQueries([QK_PROJECT_CONFIGS, projectId]),
        ]),
    },
  );
};

export const useEnableAgentConfig = (projectId: string) =>
  useProjectConfigsMutation<{ config_key: string; start: SlotStart }, { slot: AgentSlot }>(
    projectId,
    "/projects/agent-configs/enable",
  );

export const useSetSlotStatus = (projectId: string) =>
  useProjectConfigsMutation<{ slot: string; status: SlotStatus }, { slot: AgentSlot }>(
    projectId,
    "/projects/agent-configs/set-status",
  );

export const useRemoveSlot = (projectId: string) =>
  useProjectConfigsMutation<{ slot: string }, { deleted: Record<string, number> }>(
    projectId,
    "/projects/agent-configs/remove",
  );

export const useRunEveryConfig = (projectId: string) =>
  useProjectConfigsMutation<
    Record<string, never>,
    { results?: RunAllResult[]; queued?: RunAllResult[] }
  >(projectId, "/projects/agent-configs/run-all");

export const useEnableConfigOnProjects = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    { results: EnableManyResult[] },
    Error,
    { config_key: string; start: SlotStart; project_ids: string[] }
  >((body) => orRefusal(http<{ results: EnableManyResult[] }>("/agent-configs/enable-many", body)), {
    meta: { silentError: true },
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries([QK_CONFIGS]),
        qc.invalidateQueries([QK_PROJECT_CONFIGS]),
      ]),
  });
};
